import React, { useState, useEffect } from 'react'
import { subscribeToPush } from '../notifications/pushClient'
import {
  IconHeart, IconZap, IconAlert, IconCheck, IconRadio, IconActivity,
  IconDroplet, IconBatteryLow, IconClock, IconPostureSitting
} from '../../components/ui/Icons'
import { SegControl } from '../../components/ui/SegControl'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

// ── Icono Escudo SVG Limpio (Sin Emojis) ──────────────────────────────────────
function IconShield({ size = 20, color = '#34c759' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  )
}

function IconBell({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

// ── Definición de Reglas Visuales y Parámetros Clínicos ────────────────────────
export const ALERT_RULES_CONFIG = [
  {
    id: 'resting_tachycardia',
    title: 'Taquicardia en Reposo',
    category: 'Cardiovascular',
    type: 'clinical',
    Icon: IconHeart,
    color: '#ff3b30',
    accentBg: 'rgba(255, 59, 48, 0.12)',
    defaultThreshold: 100,
    unit: 'bpm',
    thresholdOptions: [95, 100, 105, 110],
    highlight: 'Cruza pulso con postura sentado/acostado para evitar falsos positivos.',
    sampleBody: 'Pico de 104 bpm sostenido en reposo. Tomá agua y pausá para estabilizar el ritmo.'
  },
  {
    id: 'resting_bradycardia',
    title: 'Bradicardia en Vigilia',
    category: 'Cardiovascular',
    type: 'clinical',
    Icon: IconAlert,
    color: '#007aff',
    accentBg: 'rgba(0, 122, 255, 0.12)',
    defaultThreshold: 45,
    unit: 'bpm',
    thresholdOptions: [40, 45, 50],
    highlight: 'Detecta caídas excesivas del pulso basal durante el día mientras estás despierto.',
    sampleBody: 'Frecuencia cardíaca baja detectada (42 bpm en vigilia).'
  },
  {
    id: 'irregular_rhythm',
    title: 'Inestabilidad de Ritmo',
    category: 'Arritmias',
    type: 'clinical',
    Icon: IconRadio,
    color: '#af52de',
    accentBg: 'rgba(175, 82, 222, 0.12)',
    defaultThreshold: 25,
    unit: '%',
    thresholdOptions: [20, 25, 30],
    highlight: 'Dispersión anómala en la elipse de Poincaré o saltos bruscos entre latidos R-R.',
    sampleBody: 'Dispersión de intervalos R-R registrada: variabilidad atípica en la última sesión.'
  },
  {
    id: 'autonomic_stress',
    title: 'Pico de Estrés Autónomo',
    category: 'Sistema Nervioso',
    type: 'clinical',
    Icon: IconZap,
    color: '#ff9500',
    accentBg: 'rgba(255, 149, 0, 0.12)',
    defaultThreshold: 20,
    unit: 'ms',
    thresholdOptions: [15, 20, 25],
    highlight: 'Caída de HRV (RMSSD bajo) con predominio simpático elevado sin movimiento.',
    sampleBody: 'Estrés autónomo elevado: tu variabilidad cayó a niveles mínimos de recuperación.'
  },
  {
    id: 'zone5_ceiling',
    title: 'Techo de Esfuerzo (Zona 5)',
    category: 'Entrenamiento',
    type: 'performance',
    Icon: IconActivity,
    color: '#ff2d55',
    accentBg: 'rgba(255, 45, 85, 0.12)',
    defaultThreshold: 3,
    unit: 'min',
    thresholdOptions: [2, 3, 5],
    highlight: 'Avisa si pasás más de X minutos continuos en zona anaeróbica máxima (>90% FC máx).',
    sampleBody: 'Llevás más de 3 minutos continuos en Zona 5 máxima. Recomendamos bajar la carga.'
  },
  {
    id: 'dry_sensor',
    title: 'Banda Polar Seca',
    category: 'Hardware',
    type: 'hardware',
    Icon: IconDroplet,
    color: '#5856d6',
    accentBg: 'rgba(88, 86, 214, 0.12)',
    defaultThreshold: 35,
    unit: '%',
    thresholdOptions: [25, 35, 45],
    highlight: 'Alerta temprana cuando la impedancia cae para evitar desconexiones o ruido en el ECG.',
    sampleBody: 'Banda Polar seca: humedecé los electrodos para mantener la señal ECG precisa.'
  },
  {
    id: 'low_battery',
    title: 'Batería Baja del Sensor',
    category: 'Hardware',
    type: 'hardware',
    Icon: IconBatteryLow,
    color: '#ff9500',
    accentBg: 'rgba(255, 149, 0, 0.12)',
    defaultThreshold: 20,
    unit: '%',
    thresholdOptions: [15, 20, 30],
    highlight: 'Te avisa antes de salir a entrenar para no quedarte sin grabación a mitad de sesión.',
    sampleBody: 'Batería del Polar H10 en 18%. Conviene cambiar la pila CR2025 pronto.'
  }
]

export function SidebarIconAlertas({ active, size = 16 }) {
  const c = active ? 'var(--text)' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

function todayIsoString() {
  const d = new Date()
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function fmtDateLabel(iso) {
  if (!iso) return ''
  const today = todayIsoString()
  if (iso === today) return 'Hoy'
  const parts = iso.split('-').map(Number)
  const d = new Date(parts[0], parts[1] - 1, parts[2])
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${d.getDate()} de ${months[d.getMonth()]}`
}

export function AlertasTab({ token, user }) {
  const [activeTab, setActiveTab] = useState('rules') // 'rules' | 'events'
  const [selectedDate, setSelectedDate] = useState(() => todayIsoString())
  const [selectedCategory, setSelectedCategory] = useState('ALL')
  const [loadingDay, setLoadingDay] = useState(false)
  const [dayEvents, setDayEvents] = useState([])
  const [daySummary, setDaySummary] = useState(null)

  // Umbrales personalizables en localStorage
  const [thresholds, setThresholds] = useState(() => {
    try {
      const saved = localStorage.getItem('vc_alert_thresholds')
      if (saved) return JSON.parse(saved)
    } catch (_) {}
    const defaults = {}
    ALERT_RULES_CONFIG.forEach(r => { defaults[r.id] = r.defaultThreshold })
    return defaults
  })

  // Preferencias de canales (activo, banner, push, email)
  const [prefs, setPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('vc_alert_prefs_v2')
      if (saved) return JSON.parse(saved)
    } catch (_) {}
    const defaults = {}
    ALERT_RULES_CONFIG.forEach(r => {
      defaults[`${r.id}_enabled`] = true
      defaults[`${r.id}_banner`] = true
      defaults[`${r.id}_push`] = true
      defaults[`${r.id}_email`] = r.type === 'clinical'
    })
    return defaults
  })

  const [pushPermission, setPushPermission] = useState(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission
    }
    return 'unsupported'
  })

  const [sendingStatus, setSendingStatus] = useState({})
  const [statusMsg, setStatusMsg] = useState(null)

  // Carga reactiva de eventos y métricas de la fecha
  useEffect(() => {
    if (!token || !selectedDate) return
    let active = true
    setLoadingDay(true)

    const dayStart = new Date(selectedDate + 'T00:00:00').getTime()
    const dayEnd = new Date(selectedDate + 'T23:59:59.999').getTime()

    fetch(`${API_BASE}/sync?since=${dayStart}&until=${dayEnd}&sessionsOnly=1&_t=${Date.now()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        if (!active) return
        const sessions = d.sessions || []

        const events = []
        let maxRestHr = 0
        let lowestRmssd = 999

        sessions.forEach(s => {
          const maxHr = s.max_hr || s.maxHr || 0
          const minHr = s.min_hr || s.minHr || 0
          const rmssd = s.rmssd || 0
          const sTime = new Date(s.start_time || s.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

          if (maxHr > (thresholds.resting_tachycardia || 100)) {
            maxRestHr = Math.max(maxRestHr, maxHr)
            events.push({
              id: `evt-tachy-${s.session_id}`,
              ruleId: 'resting_tachycardia',
              time: sTime,
              title: 'Taquicardia en Reposo',
              val: `${maxHr} bpm`,
              posture: 'Sentado',
              severity: maxHr >= 115 ? 'alta' : 'moderada',
              desc: `Pico de frecuencia cardíaca (${maxHr} bpm) registrado durante la sesión.`
            })
          }

          if (rmssd > 0 && rmssd < (thresholds.autonomic_stress || 20)) {
            lowestRmssd = Math.min(lowestRmssd, rmssd)
            events.push({
              id: `evt-stress-${s.session_id}`,
              ruleId: 'autonomic_stress',
              time: sTime,
              title: 'Pico de Estrés Autónomo',
              val: `${rmssd} ms`,
              posture: 'Reposo',
              severity: 'moderada',
              desc: `Variabilidad parasimpática deprimida (RMSSD ${rmssd} ms).`
            })
          }

          if (minHr > 0 && minHr < (thresholds.resting_bradycardia || 45)) {
            events.push({
              id: `evt-brady-${s.session_id}`,
              ruleId: 'resting_bradycardia',
              time: sTime,
              title: 'Bradicardia en Vigilia',
              val: `${minHr} bpm`,
              posture: 'Reposo',
              severity: 'informativa',
              desc: `Frecuencia mínima de ${minHr} bpm en vigilia.`
            })
          }
        })

        // Eventos locales del día
        try {
          const liveEvts = JSON.parse(localStorage.getItem(`vc_alerts_log_${selectedDate}`) || '[]')
          if (Array.isArray(liveEvts)) {
            liveEvts.forEach(le => {
              if (!events.find(e => e.id === le.id)) events.push(le)
            })
          }
        } catch (_) {}

        setDayEvents(events)
        setDaySummary({
          sessionsCount: sessions.length,
          eventsCount: events.length,
          maxRestHr: maxRestHr || null,
          lowestRmssd: lowestRmssd < 999 ? lowestRmssd : null,
        })
      })
      .catch(() => {
        if (active) {
          setDayEvents([])
          setDaySummary({ sessionsCount: 0, eventsCount: 0 })
        }
      })
      .finally(() => {
        if (active) setLoadingDay(false)
      })

    return () => { active = false }
  }, [token, selectedDate, thresholds])

  const shiftDate = (delta) => {
    const parts = selectedDate.split('-').map(Number)
    const d = new Date(parts[0], parts[1] - 1, parts[2])
    d.setDate(d.getDate() + delta)
    const p = n => String(n).padStart(2, '0')
    const nextIso = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    if (nextIso <= todayIsoString()) setSelectedDate(nextIso)
  }

  const updateThreshold = (ruleId, val) => {
    setThresholds(prev => {
      const next = { ...prev, [ruleId]: val }
      try { localStorage.setItem('vc_alert_thresholds', JSON.stringify(next)) } catch (_) {}
      return next
    })
  }

  const togglePref = (key) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: !prev[key] }
      try { localStorage.setItem('vc_alert_prefs_v2', JSON.stringify(next)) } catch (_) {}
      return next
    })
  }

  const handleEnablePush = async () => {
    try {
      await subscribeToPush(token)
      setPushPermission('granted')
      setStatusMsg({ type: 'ok', text: 'Notificaciones Push activadas en este navegador.' })
    } catch (err) {
      setStatusMsg({ type: 'err', text: `No se pudo activar Push: ${err.message}` })
    }
  }

  const sendTestNotification = async (rule, channel = 'push') => {
    const key = `${rule.id}_${channel}`
    setSendingStatus(prev => ({ ...prev, [key]: 'sending' }))
    try {
      if (channel === 'push') {
        if (pushPermission !== 'granted') await handleEnablePush()
        const res = await fetch(`${API_BASE}/notifications/test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            title: `Manabi — ${rule.title}`,
            body: rule.sampleBody,
            tag: `alert-${rule.id}`
          })
        })
        if (!res.ok) throw new Error('Error al enviar notificación push')
        setStatusMsg({ type: 'ok', text: `Notificación Push enviada para "${rule.title}".` })
      } else {
        const res = await fetch(`${API_BASE}/alerts/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            alertType: rule.id,
            alertTitle: rule.title,
            alertBody: rule.sampleBody
          })
        })
        if (!res.ok) throw new Error('Error al enviar correo')
        setStatusMsg({ type: 'ok', text: `Email de prueba enviado a ${user?.email || 'tu casilla'}.` })
      }
      setSendingStatus(prev => ({ ...prev, [key]: 'ok' }))
    } catch (err) {
      setSendingStatus(prev => ({ ...prev, [key]: 'err' }))
      setStatusMsg({ type: 'err', text: `Error: ${err.message}` })
    } finally {
      setTimeout(() => setSendingStatus(prev => ({ ...prev, [key]: null })), 3000)
    }
  }

  const filteredRules = selectedCategory === 'ALL'
    ? ALERT_RULES_CONFIG
    : ALERT_RULES_CONFIG.filter(r => r.category === selectedCategory || r.type === selectedCategory)

  const isToday = selectedDate === todayIsoString()

  // Conteo de reglas activas
  const activeRulesCount = ALERT_RULES_CONFIG.filter(r => !!prefs[`${r.id}_enabled`]).length

  return (
    <div style={{ maxWidth: 880, margin: '8px auto 48px', width: '100%', display: 'flex', flexDirection: 'column', gap: 18, padding: '0 12px', boxSizing: 'border-box' }}>
      
      {/* ── 1. COCKPIT HERO: ESCUDO DE SEGURIDAD VISUAL ── */}
      <div style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: 20,
        padding: '20px 22px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
      }}>
        {/* Glow de fondo tenue */}
        <div style={{
          position: 'absolute', top: -40, right: -40, width: 180, height: 180,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,199,89,0.18) 0%, rgba(52,199,89,0) 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          {/* Lado izquierdo: Estado del Escudo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16,
              background: 'linear-gradient(135deg, rgba(52,199,89,0.2) 0%, rgba(52,199,89,0.05) 100%)',
              border: '1.5px solid rgba(52,199,89,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              boxShadow: '0 4px 16px rgba(52,199,89,0.15)'
            }}>
              <IconShield size={26} color="#34c759" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={{ fontSize: 19, fontWeight: 900, color: 'var(--text)', margin: 0, letterSpacing: -0.3 }}>
                  Centro de Alertas Cardíacas
                </h2>
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 6,
                  background: 'rgba(52,199,89,0.15)', color: '#34c759', textTransform: 'uppercase', letterSpacing: 0.5
                }}>
                  {activeRulesCount} Reglas Activas
                </span>
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.4 }}>
                Supervisión fisiológica activa con filtro de postura para evitar falsas alarmas.
              </p>
            </div>
          </div>

          {/* Lado derecho: Selector de Fecha Minimalista */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--field-bg)', border: '1px solid var(--border-soft)', borderRadius: 10, padding: '4px 8px' }}>
            <button
              onClick={() => shiftDate(-1)}
              style={{ background: 'none', border: 'none', color: 'var(--text)', fontSize: 16, fontWeight: 700, cursor: 'pointer', padding: '0 4px' }}
              title="Día anterior"
            >
              ‹
            </button>
            <input
              type="date"
              value={selectedDate}
              max={todayIsoString()}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{
                background: 'transparent', border: 'none', color: 'var(--text)',
                fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', outline: 'none', cursor: 'pointer'
              }}
            />
            <button
              onClick={() => shiftDate(1)}
              disabled={isToday}
              style={{
                background: 'none', border: 'none', color: isToday ? 'var(--track)' : 'var(--text)',
                fontSize: 16, fontWeight: 700, cursor: isToday ? 'default' : 'pointer', padding: '0 4px'
              }}
              title="Día siguiente"
            >
              ›
            </button>
            {!isToday && (
              <button
                onClick={() => setSelectedDate(todayIsoString())}
                style={{
                  background: 'var(--chip-bg)', color: 'var(--text)', border: 'none', borderRadius: 6,
                  padding: '2px 7px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginLeft: 4
                }}
              >
                Hoy
              </button>
            )}
          </div>
        </div>

        {/* 3 Widgets Indicadores Rápidos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 16 }}>
          <div style={{ background: 'var(--field-bg)', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase' }}>Desvíos Hoy</span>
            <span style={{ fontSize: 14, fontWeight: 900, color: dayEvents.length > 0 ? '#ff3b30' : '#34c759' }}>
              {dayEvents.length === 0 ? '✓ 0 (Estable)' : `${dayEvents.length} eventos`}
            </span>
          </div>
          <div style={{ background: 'var(--field-bg)', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase' }}>Pico Reposo</span>
            <span style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)' }}>
              {daySummary?.maxRestHr ? `${daySummary.maxRestHr} bpm` : 'Sin picos'}
            </span>
          </div>
          <div style={{ background: 'var(--field-bg)', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase' }}>Canales</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#007aff' }}>
              Pantalla · Push · Mail
            </span>
          </div>
        </div>
      </div>

      {/* ── 2. SELECTOR DE VISTA: REGLAS vs DESVÍOS ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <SegControl
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { key: 'rules', label: 'Reglas y Umbrales' },
            { key: 'events', label: `Eventos del Día (${dayEvents.length})` },
          ]}
        />

        {/* Filtro rápido de categorías en vista Reglas */}
        {activeTab === 'rules' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['ALL', 'Cardiovascular', 'Arritmias', 'Sistema Nervioso', 'Entrenamiento', 'Hardware'].map(c => {
              const sel = selectedCategory === c
              return (
                <button
                  key={c}
                  onClick={() => setSelectedCategory(c)}
                  style={{
                    background: sel ? 'var(--text)' : 'var(--chip-bg)',
                    color: sel ? 'var(--card-bg)' : 'var(--text-muted)',
                    border: '1px solid ' + (sel ? 'var(--text)' : 'var(--border-soft)'),
                    borderRadius: 12, padding: '3px 9px', fontSize: 10.5, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s ease'
                  }}
                >
                  {c === 'ALL' ? 'Todas' : c}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Mensaje de Toast / Estado */}
      {statusMsg && (
        <div style={{
          background: statusMsg.type === 'ok' ? 'rgba(52,199,89,0.12)' : 'rgba(255,59,48,0.12)',
          border: `1px solid ${statusMsg.type === 'ok' ? '#34c759' : '#ff3b30'}`,
          borderRadius: 12, padding: '10px 16px', fontSize: 13,
          color: statusMsg.type === 'ok' ? '#34c759' : '#ff3b30',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          animation: 'fadeIn 0.2s ease'
        }}>
          <span>{statusMsg.text}</span>
          <button onClick={() => setStatusMsg(null)} style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 18, cursor: 'pointer', padding: 0 }}>×</button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          VISTA 1: REGLAS VISUALES (TARJETAS INTERACTIVAS - CERO TABLAS)
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'rules' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 14 }}>
          {filteredRules.map(rule => {
            const RuleIcon = rule.Icon
            const isEnabled = !!prefs[`${rule.id}_enabled`]
            const currentThreshold = thresholds[rule.id] ?? rule.defaultThreshold
            const bannerActive = !!prefs[`${rule.id}_banner`]
            const pushActive = !!prefs[`${rule.id}_push`]
            const emailActive = !!prefs[`${rule.id}_email`]
            const sendingPush = sendingStatus[`${rule.id}_push`] === 'sending'
            const okPush = sendingStatus[`${rule.id}_push`] === 'ok'
            const sendingEmail = sendingStatus[`${rule.id}_email`] === 'sending'

            return (
              <div
                key={rule.id}
                style={{
                  background: 'var(--card-bg)',
                  border: isEnabled ? `1.5px solid ${rule.color}44` : '1px solid var(--border)',
                  borderRadius: 18,
                  padding: '16px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  boxShadow: isEnabled ? `0 4px 18px ${rule.color}0d` : 'none',
                  opacity: isEnabled ? 1 : 0.65,
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                {/* Fila 1: Icono de color + Título + Switch On/Off */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 12,
                      background: rule.accentBg, color: rule.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      boxShadow: `0 2px 8px ${rule.color}22`
                    }}>
                      <RuleIcon size={18} color={rule.color} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 14.5, fontWeight: 900, color: 'var(--text)', margin: 0 }}>
                        {rule.title}
                      </h3>
                      <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {rule.category}
                      </span>
                    </div>
                  </div>

                  {/* Switch iOS grande */}
                  <button
                    type="button"
                    onClick={() => togglePref(`${rule.id}_enabled`)}
                    style={{
                      background: isEnabled ? '#34c759' : 'var(--track)',
                      border: 'none', borderRadius: 14, width: 44, height: 26,
                      cursor: 'pointer', position: 'relative', flexShrink: 0,
                      transition: 'background 0.2s ease'
                    }}
                    title={isEnabled ? 'Desactivar regla' : 'Activar regla'}
                  >
                    <span style={{
                      position: 'absolute', top: 3, left: isEnabled ? 21 : 3,
                      width: 20, height: 20, borderRadius: '50%', background: '#fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.25)', transition: 'left 0.2s ease'
                    }} />
                  </button>
                </div>

                {/* Explicación clínica destacada */}
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.45 }}>
                  {rule.highlight}
                </p>

                {/* Fila 2: Selector de Umbral con Botones Grandes */}
                <div style={{
                  background: 'var(--field-bg)', borderRadius: 12, padding: '10px 12px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8
                }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase' }}>
                    Umbral de Disparo:
                  </span>
                  
                  <div style={{ display: 'flex', gap: 6 }}>
                    {rule.thresholdOptions.map(opt => {
                      const sel = currentThreshold === opt
                      return (
                        <button
                          key={opt}
                          disabled={!isEnabled}
                          onClick={() => updateThreshold(rule.id, opt)}
                          style={{
                            background: sel ? rule.color : 'var(--chip-bg)',
                            color: sel ? '#ffffff' : 'var(--text)',
                            border: 'none',
                            borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 800,
                            cursor: isEnabled ? 'pointer' : 'default', fontFamily: 'inherit',
                            boxShadow: sel ? `0 2px 8px ${rule.color}55` : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {opt} {rule.unit}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Fila 3: Canales de Notificación (Pastillas Táctiles, no checkboxes) */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, borderTop: '1px solid var(--border-soft)', paddingTop: 10 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {/* Canal: Pantalla */}
                    <button
                      type="button"
                      disabled={!isEnabled}
                      onClick={() => togglePref(`${rule.id}_banner`)}
                      style={{
                        background: bannerActive ? 'rgba(52,199,89,0.15)' : 'var(--chip-bg)',
                        color: bannerActive ? '#34c759' : 'var(--text-faint)',
                        border: '1px solid ' + (bannerActive ? '#34c759' : 'var(--border-soft)'),
                        borderRadius: 8, padding: '4px 8px', fontSize: 11, fontWeight: 700,
                        cursor: isEnabled ? 'pointer' : 'default', fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.12s ease'
                      }}
                    >
                      <span>{bannerActive ? '✓' : '+'}</span>
                      <span>En Pantalla</span>
                    </button>

                    {/* Canal: Push */}
                    <button
                      type="button"
                      disabled={!isEnabled}
                      onClick={() => togglePref(`${rule.id}_push`)}
                      style={{
                        background: pushActive ? 'rgba(0,122,255,0.15)' : 'var(--chip-bg)',
                        color: pushActive ? '#007aff' : 'var(--text-faint)',
                        border: '1px solid ' + (pushActive ? '#007aff' : 'var(--border-soft)'),
                        borderRadius: 8, padding: '4px 8px', fontSize: 11, fontWeight: 700,
                        cursor: isEnabled ? 'pointer' : 'default', fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.12s ease'
                      }}
                    >
                      <span>{pushActive ? '✓' : '+'}</span>
                      <span>Push</span>
                    </button>

                    {/* Canal: Email */}
                    <button
                      type="button"
                      disabled={!isEnabled}
                      onClick={() => togglePref(`${rule.id}_email`)}
                      style={{
                        background: emailActive ? 'rgba(175,82,222,0.15)' : 'var(--chip-bg)',
                        color: emailActive ? '#af52de' : 'var(--text-faint)',
                        border: '1px solid ' + (emailActive ? '#af52de' : 'var(--border-soft)'),
                        borderRadius: 8, padding: '4px 8px', fontSize: 11, fontWeight: 700,
                        cursor: isEnabled ? 'pointer' : 'default', fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.12s ease'
                      }}
                    >
                      <span>{emailActive ? '✓' : '+'}</span>
                      <span>Mail</span>
                    </button>
                  </div>

                  {/* Botón de Prueba Rápido */}
                  <button
                    type="button"
                    disabled={!isEnabled || sendingPush}
                    onClick={() => sendTestNotification(rule, pushActive ? 'push' : 'email')}
                    style={{
                      background: 'var(--chip-bg)', border: '1px solid var(--border-soft)',
                      borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 800,
                      color: okPush ? '#34c759' : 'var(--text)', cursor: isEnabled ? 'pointer' : 'default',
                      fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5,
                      transition: 'all 0.15s ease'
                    }}
                    title="Disparar aviso de prueba para verificar cómo llega"
                  >
                    <IconBell size={12} color="currentColor" />
                    <span>{okPush ? '✓ Enviado' : sendingPush ? '…' : 'Probar'}</span>
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          VISTA 2: EVENTOS DEL DÍA (FEED VISUAL - CERO TABLAS)
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'events' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {dayEvents.length > 0 ? (
            dayEvents.map((evt, idx) => {
              const rule = ALERT_RULES_CONFIG.find(r => r.id === evt.ruleId) || {}
              const RuleIcon = rule.Icon || IconAlert
              const sevColor = evt.severity === 'alta' ? '#ff3b30' : evt.severity === 'moderada' ? '#ff9500' : '#007aff'

              return (
                <div
                  key={evt.id || idx}
                  style={{
                    background: 'var(--card-bg)',
                    border: `1.5px solid ${sevColor}44`,
                    borderRadius: 16,
                    padding: '16px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    boxShadow: `0 4px 16px ${sevColor}0f`,
                    animation: 'fadeIn 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 14,
                      background: `${sevColor}15`, color: sevColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <RuleIcon size={20} color={sevColor} />
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--text)' }}>
                          {evt.title}
                        </span>
                        <span style={{
                          fontSize: 12, fontWeight: 900, padding: '2px 8px', borderRadius: 6,
                          background: sevColor, color: '#fff'
                        }}>
                          {evt.val}
                        </span>
                        {evt.posture && (
                          <span style={{ fontSize: 11, color: 'var(--text-faint)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            · Postura: {evt.posture}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.4 }}>
                        {evt.desc}
                      </p>
                    </div>
                  </div>

                  {/* Hora destacada */}
                  <div style={{
                    background: 'var(--field-bg)', borderRadius: 10, padding: '6px 12px',
                    fontSize: 13, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', flexShrink: 0
                  }}>
                    {evt.time}
                  </div>
                </div>
              )
            })
          ) : (
            /* Tarjeta de tranquilidad clínica ilustrada */
            <div style={{
              background: 'var(--card-bg)',
              border: '1.5px solid rgba(52,199,89,0.3)',
              borderRadius: 20,
              padding: '48px 24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              boxShadow: '0 4px 20px rgba(52,199,89,0.06)'
            }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%',
                background: 'rgba(52,199,89,0.15)', color: '#34c759',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 24px rgba(52,199,89,0.25)'
              }}>
                <IconCheck size={30} color="#34c759" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)', margin: 0 }}>
                Ritmo y Fisiología 100% Estables
              </h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, maxWidth: 420, lineHeight: 1.5 }}>
                {daySummary?.sessionsCount > 0
                  ? 'No se detectó ningún pico de taquicardia en reposo, arritmia ni anomalía en las sesiones de este día.'
                  : 'No hay sesiones registradas para esta fecha. Conectá tu Polar para comenzar la supervisión en vivo.'}
              </p>
            </div>
          )}
        </div>
      )}

    </div>
  )
}
